// Copyright (c) Mysten Labs, Inc.
// SPDX-License-Identifier: Apache-2.0

import { formatDate } from '@mysten/core';
import { useSafeClientQuery } from '@safecoin/dapp-kit';
import { Heading, Text } from '@mysten/ui';
import BigNumber from 'bignumber.js';
import clsx from 'clsx';
import { type ReactNode, useMemo } from 'react';

import {
	useCoinDetail,
	useCoinPriceHistory,
	type CoinPricePoint,
} from '~/hooks/useCoins';
import {
	formatPercentChange,
	formatSupplyAmount,
	formatUsd,
	formatUsdPlain,
} from '~/pages/coins/format';
import { Card } from '~/ui/Card';
import { ReactComponent as InfoSvg } from '~/ui/icons/info_10x10.svg';
import { Tooltip } from '~/ui/Tooltip';

// The native SAFE coin — the metrics bar is sourced from its safecoin market data.
const NATIVE_COIN = '0x0000000000000000000000000000000000000000000000000000000000000002::safe::SAFE';

function ChangeText({ value }: { value: number | null | undefined }) {
	const change = formatPercentChange(value ?? null);
	if (!change) return null;
	return (
		<span
			className={clsx(
				'text-bodySmall font-medium',
				change.positive ? 'text-success-dark' : 'text-issue-dark',
			)}
		>
			{change.text}
		</span>
	);
}

function Metric({
	label,
	tooltip,
	value,
	secondary,
	children,
}: {
	label: string;
	tooltip?: string;
	value: ReactNode;
	secondary?: ReactNode;
	children?: ReactNode;
}) {
	return (
		<div className="flex min-w-[150px] flex-1 items-start justify-between gap-3 border-l border-gray-45 px-6 py-4 first:border-l-0">
			<div className="flex flex-col gap-1">
				<div className="flex items-center gap-1">
					<Text variant="caption/medium" color="steel-dark">
						{label}
					</Text>
					{tooltip && (
						<Tooltip tip={tooltip}>
							<InfoSvg />
						</Tooltip>
					)}
				</div>
				<Heading variant="heading6/semibold" color="steel-darker">
					{value}
				</Heading>
				{secondary}
			</div>
			{children}
		</div>
	);
}

// A tiny axis-free price sparkline drawn straight to SVG (no tooltip / no axes,
// unlike AreaGraph which needs ≥100×100 and renders chrome).
function Sparkline({ points }: { points: CoinPricePoint[] }) {
	const width = 128;
	const height = 52;
	if (points.length < 2) return null;

	const xs = points.map((p) => p.t);
	const ys = points.map((p) => p.price);
	const minX = Math.min(...xs);
	const maxX = Math.max(...xs);
	const minY = Math.min(...ys);
	const maxY = Math.max(...ys);
	const spanX = maxX - minX || 1;
	const spanY = maxY - minY || 1;
	const pad = 3;
	const sx = (x: number) => pad + ((x - minX) / spanX) * (width - 2 * pad);
	const sy = (y: number) => height - pad - ((y - minY) / spanY) * (height - 2 * pad);

	const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${sx(p.t).toFixed(1)},${sy(p.price).toFixed(1)}`).join(' ');
	const area = `${line} L${sx(maxX).toFixed(1)},${height} L${sx(minX).toFixed(1)},${height} Z`;
	const positive = ys[ys.length - 1] >= ys[0];
	const stroke = positive ? '#008C65' : '#EB5A29';

	return (
		<div className="flex flex-col items-end gap-0.5">
			<svg width={width} height={height} className="overflow-visible">
				<defs>
					<linearGradient id="sparklineFill" x1="0" y1="0" x2="0" y2="1">
						<stop offset="0%" stopColor={stroke} stopOpacity="0.24" />
						<stop offset="100%" stopColor={stroke} stopOpacity="0" />
					</linearGradient>
				</defs>
				<path d={area} fill="url(#sparklineFill)" stroke="none" />
				<path d={line} fill="none" stroke={stroke} strokeWidth={1.5} strokeLinejoin="round" />
			</svg>
			<div className="flex w-full justify-between">
				<Text variant="captionSmall/medium" color="steel">
					{formatDate(new Date(minX), ['day', 'month'])}
				</Text>
				<Text variant="captionSmall/medium" color="steel">
					{formatDate(new Date(maxX), ['day', 'month'])}
				</Text>
			</div>
		</div>
	);
}

export function MarketMetricsBar() {
	const { data } = useCoinDetail(NATIVE_COIN);
	const { data: history } = useCoinPriceHistory(NATIVE_COIN, '30d');
	const { data: systemState } = useSafeClientQuery('getLatestSafeSystemState', {});

	const coin = data?.coin;
	const symbol = coin?.symbol ?? 'SAFE';
	const decimals = coin?.decimals ?? 9;

	// Total stake = Σ each active validator's staking-pool balance (SAFELET base units).
	const totalStake = useMemo(() => {
		if (!systemState?.activeValidators) return null;
		return systemState.activeValidators.reduce(
			(acc, v) => acc.plus(v.stakingPoolSafeBalance ?? 0),
			new BigNumber(0),
		);
	}, [systemState]);

	const stakeUsd =
		totalStake && coin?.priceUsd != null
			? totalStake.shiftedBy(-decimals).times(coin.priceUsd).toNumber()
			: null;

	const withSymbol = (amount: string | null) => (amount ? `${amount} ${symbol}` : '--');

	return (
		<Card bg="white" border="gray45" rounded="xl" spacing="none">
			<div className="flex flex-wrap items-stretch">
				<Metric
					label="Market Cap"
					tooltip="Price × circulating supply"
					value={coin?.marketCapUsd != null ? formatUsd(coin.marketCapUsd) : '--'}
					secondary={<ChangeText value={coin?.priceChange?.h24} />}
				/>
				<Metric
					label="Circulating Supply"
					tooltip="Liquid supply in circulation"
					value={withSymbol(formatSupplyAmount(coin?.circulatingSupply ?? null, decimals))}
				/>
				<Metric
					label="Supply"
					tooltip="Total supply and its USD value"
					value={withSymbol(formatSupplyAmount(coin?.totalSupply ?? null, decimals))}
					secondary={
						<Text variant="bodySmall/medium" color="steel">
							{coin?.fdvUsd != null ? `${formatUsdPlain(coin.fdvUsd)} USD` : '--'}
						</Text>
					}
				/>
				<Metric
					label="Total Stake"
					tooltip="Total SAFE staked across active validators"
					value={withSymbol(
						totalStake ? formatSupplyAmount(totalStake.toFixed(0), decimals) : null,
					)}
					secondary={
						<Text variant="bodySmall/medium" color="steel">
							{stakeUsd != null ? `${formatUsdPlain(stakeUsd)} USD` : '--'}
						</Text>
					}
				/>
				<Metric
					label="Safe Price"
					value={coin?.priceUsd != null ? formatUsd(coin.priceUsd) : '--'}
					secondary={
						<span className="w-fit rounded bg-gray-40 px-1.5 py-0.5 text-captionSmall font-medium text-steel-dark">
							24H
						</span>
					}
				>
					{history?.points?.length ? <Sparkline points={history.points} /> : null}
				</Metric>
			</div>
		</Card>
	);
}
